import { useQueryClient } from "@tanstack/react-query";
import { PortalNotification, GetOrderResponse, PortalNotificationReference, PortalOrder, PayloadTransactionSummary, GetConfigResponse } from "../types";
import { getPageUrl } from "../utils";
import { apiRoutes } from "./apiRoutes";

type NotificationInputProps = {
  orderKey: string,
  notificationId: string
  search: string,
}

type UnauthedNotificationInputProps = {
  signingHash: string
}

type NotificationPatch = {
  patch: Partial<PortalNotification>
}

type PayloadMetaDataPatch = {
  patch: PayloadTransactionSummary
}

type OrderNotificationPatch = {
  patch: Partial<PortalNotificationReference>
}

type PortalOrderInputProps = {
  orderKey: string,
  search: string,
}

type PortalOrderPatch = {
  patch: Partial<PortalOrder>
}

export function useOptimisticUpdateUnauthedNotification() {
  const queryClient = useQueryClient()
  
  const optimisitcUpdateUnauthedNotification = ({ signingHash, patch }: UnauthedNotificationInputProps & NotificationPatch) => {
    queryClient.setQueryData(
      [apiRoutes.getUnauthedNotification(signingHash)],
      (notification?: PortalNotification) => {
        //If there is no existing notification and we are changing the query cache, then I assume it's a whole PortalNotification
        if (!notification) return patch as PortalNotification
        return { ...notification, ...patch }
      }
    )
  }
  return { optimisitcUpdateUnauthedNotification }
}

export function useOptimisticUpdateNotification() {
  const queryClient = useQueryClient();
  
  const optimisticUpdateNotification = ({ orderKey, notificationId, search, patch }: NotificationInputProps & NotificationPatch) => {
    queryClient.setQueryData(
      [apiRoutes.getNotification(orderKey, notificationId, search)],
      (notification?: PortalNotification) => {
        if (!notification) return notification
        return { ...notification, ...patch }
      }
    )
  }
  return { optimisticUpdateNotification }
}

export function useOptimisticUpdateOrderNotification() {
  const queryClient = useQueryClient();
  const optimisticUpdateOrderNotification = ({ orderKey, notificationId, search, patch }: NotificationInputProps & OrderNotificationPatch) => 
    queryClient.setQueryData(
      [apiRoutes.getOrder(orderKey, search)],
      (order?: GetOrderResponse) => {
        if (!order) return order
        const optimisticUpdatedNotifications = order.Notifications.map(n => n.NotificationId === notificationId ? { ...n, ...patch } : n)
        return { ...order, Notifications: optimisticUpdatedNotifications }
      }
    )
  return { optimisticUpdateOrderNotification }
}

export function useOptimisticUpdateNotificationAndOrderNotification() {
  const { optimisticUpdateNotification } = useOptimisticUpdateNotification();
  const { optimisticUpdateOrderNotification } = useOptimisticUpdateOrderNotification();

  const optimisticUpdateNotificationAndOrderNotification = (args: NotificationInputProps & OrderNotificationPatch & NotificationPatch) => {
    optimisticUpdateNotification(args);
    optimisticUpdateOrderNotification(args);
  };

  return { optimisticUpdateNotificationAndOrderNotification };
}

export function useOptimisticUpdatePortalOrder() {
  const queryClient = useQueryClient();
  const optimisticUpdatePortalOrder = ({ orderKey, search, patch }:PortalOrderInputProps & PortalOrderPatch) => {
    queryClient.setQueryData(
      [apiRoutes.getOrder(orderKey, search)],
      (order?: GetOrderResponse) => {
        if (!order) return order;
        return { ...order, Order: { ...order.Order as PortalOrder, ...patch } };
      }
    );
  };
  return { optimisticUpdatePortalOrder }
}

export function useOptimisticUpdateOrderNotifications() {
  const queryClient = useQueryClient();
  const optimisticUpdateOrderNotifications = ({ orderKey, search, patch }: PortalOrderInputProps & { patch: PortalNotificationReference[] }) => {
    queryClient.setQueryData(
      [apiRoutes.getOrder(orderKey, search)],
      (order?: GetOrderResponse) => {
        if (!order) return order;
        return { ...order, Notifications: patch };
      }
    );
  };
  return { optimisticUpdateOrderNotifications };
}

export function useOptimisticUpdatePayloadMetaData() {
  const queryClient = useQueryClient();
  const optimisticUpdatePayloadMetaData = ({ orderKey, notificationId, search, patch }: NotificationInputProps & PayloadMetaDataPatch) =>
    queryClient.setQueryData(
      [apiRoutes.getPayloadMetaData(orderKey, notificationId, search)],
      (payloadMetaData?: PayloadTransactionSummary[]) => {
        return [ ...(payloadMetaData ?? []), patch ]
      }
    )
  return { optimisticUpdatePayloadMetaData }
}

export function useOptimisticUpdateGetConfig() {
  const pageUrl = getPageUrl()
  const queryClient = useQueryClient()
  const optimisticUpdateGetConfig = () => {
    queryClient.setQueryData(
      [apiRoutes.getConfig(pageUrl)],
      (config?: GetConfigResponse) => {
        if (!config) return config
        return { ...config, userAcceptedTerms: true }
      }
    )
  }
  return { optimisticUpdateGetConfig }
}
